import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  AgendaAvailableSlotItemDataFragment,
  ConsultType,
} from '../../../data/types/api/graphql';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { AgendaAvailableAppointmentSlotListItem } from './agenda-available-appointment-slot-list-item';

type AgendaAvailableAppointmentSlotListProps = {
  healthProfessionalId: string | null | undefined;
  items: AgendaAvailableSlotItemDataFragment[];
  consultType: ConsultType;
  showAsRange?: boolean;
  multiple?: boolean;
  compact?: boolean;
  values?: AgendaAvailableSlotItemDataFragment[];
  value?: {
    healthProfessionalId: string | null | undefined;
    selectedSlots: AgendaAvailableSlotItemDataFragment[];
  };
  onClick?: (values: AgendaAvailableSlotItemDataFragment[]) => void;
};

const toDate = (dateString: string | number) => new Date(dateString);
const toTime = (dateString: string | number) => {
  const dateTime = toDate(dateString);

  const hours = dateTime.getHours().toString().padStart(2, '0');
  const minutes = dateTime.getMinutes().toString().padStart(2, '0');

  const formattedTime = `${hours}:${minutes}`;
  return formattedTime;
};

export function AgendaAvailableAppointmentSlotList({
  healthProfessionalId,
  values,
  multiple,
  consultType,
  showAsRange,
  compact,
  onClick,
  value,
  ...props
}: AgendaAvailableAppointmentSlotListProps) {
  const [selected, setSelected] = useState<
    AgendaAvailableSlotItemDataFragment[]
  >(value?.selectedSlots ?? []);

  const items = useMemo(() => props.items, [props.items]);

  // on healthprofessiona change empty selected
  useEffect(() => {
    if (
      healthProfessionalId !== value?.healthProfessionalId &&
      selected.length > 0
    ) {
      setSelected([]);
    }
  }, [healthProfessionalId, selected.length, value?.healthProfessionalId]);

  // range check
  const checkIfRange = useCallback(
    (slot: AgendaAvailableSlotItemDataFragment) => {
      if (showAsRange === true) {
        return true;
      }
      return slot.existing;
    },
    [showAsRange]
  );

  const highlighted = useMemo(() => {
    const itemsBefore: AgendaAvailableSlotItemDataFragment[] = [];
    const itemsAfter: AgendaAvailableSlotItemDataFragment[] = [];

    if (selected.length > 0 && multiple && items.length > 0) {
      const firstSelected = selected[0];
      const lastSelected = selected.at(-1);
      const firstSelectedIndex = items.indexOf(firstSelected);
      const lastSelectedIndex = lastSelected
        ? items.indexOf(lastSelected)
        : firstSelectedIndex;

      let firstSelectedStartTime = toTime(firstSelected.startTime);
      let lastSelectedEndTime = lastSelected
        ? toTime(lastSelected.endTime)
        : toTime(firstSelected.endTime);

      // Iterate backwards to find all consecutive items before `firstSelected`
      for (let i = firstSelectedIndex - 1; i >= 0; i -= 1) {
        const currentItem = items[i];
        const currentItemEndTime = toTime(currentItem.endTime);

        // If consecutive, add to itemsBefore; otherwise, break
        if (currentItemEndTime === firstSelectedStartTime) {
          itemsBefore.push(currentItem);
          firstSelectedStartTime = toTime(currentItem.startTime); // Constantly compare with each previous item's start time
        } else {
          break; // Stop if not consecutive
        }
      }

      // Iterate forwards to find all consecutive items after `lastSelected`
      for (let i = lastSelectedIndex + 1; i < items.length; i += 1) {
        const currentItem = items[i];
        const currentItemStartTime = toTime(currentItem.startTime);

        // If consecutive, add to itemsAfter; otherwise, break
        if (currentItemStartTime === lastSelectedEndTime) {
          itemsAfter.push(currentItem);
          lastSelectedEndTime = toTime(currentItem.endTime); // Constantly compare with each next item's end time
        } else {
          break; // Stop if not consecutive
        }
      }
    }

    return [...itemsBefore.reverse(), ...itemsAfter];
  }, [items, multiple, selected]);

  const onSelect = useCallback(
    (option: AgendaAvailableSlotItemDataFragment) => {
      const update: AgendaAvailableSlotItemDataFragment[] = [];
      // multiple
      if (multiple) {
        // current
        const currentIndex = selected.findIndex(
          s => toTime(s.startTime) === toTime(option.startTime)
        );

        if (currentIndex === -1) {
          // clear existing then add

          update.push(option);
          const optionStartTime = toDate(option.startTime).getTime();
          const optionEndTime = toDate(option.endTime).getTime();

          // add
          if (
            selected.length === 0 ||
            selected.some(
              i =>
                toTime(i.startTime) === toTime(optionEndTime) ||
                toTime(i.endTime) === toTime(optionStartTime)
            )
          ) {
            update.push(...selected);
          }
        } else if (currentIndex === 0 || currentIndex === selected.length - 1) {
          update.push(...selected);
          update.splice(currentIndex, 1);
        }

        // sort
        update.sort(
          (a, b) =>
            toDate(a.startTime).getTime() - toDate(b.startTime).getTime()
        );
      } else {
        // single
        while (update.length > 0) {
          update.pop();
        }
        // if not contains add
        if (!selected.includes(option)) {
          update.push(option);
        }
      }
      setSelected(update);
      onClick?.(update);
    },
    [multiple, onClick, selected]
  );

  if (isEmpty(items)) {
    return (
      <p>
        <CustomFormattedMessage id="common.no-data" />
      </p>
    );
  }

  return (
    <div className="content-list">
      {items.map(option => (
        <div key={option.id} className="content-list__item">
          <AgendaAvailableAppointmentSlotListItem
            consultType={consultType}
            showAsRange={checkIfRange(option)}
            agendaSlotItem={option}
            highlight={highlighted.map(x => x.id).includes(option.id)}
            compact={compact}
            active={selected?.includes(option)}
            onClick={onSelect}
          />
        </div>
      ))}
    </div>
  );
}
